'use client';

/**
 * Blocking cookie consent gate (GitLab #649).
 *
 * Two jobs in one dialog:
 *   - Before a choice exists it blocks the page. Escape, the overlay and
 *     the close button are all disabled, because a dismissable banner is
 *     an implied consent and PECR does not allow one (AC1).
 *   - Once a choice exists the same dialog reopens from the footer's
 *     "Cookie settings" link, dismissable, so a customer can change or
 *     withdraw their mind as easily as they gave it (AC10).
 *
 * "Accept all" and "Essential only" are the same variant and size on
 * purpose: refusing must be no harder than accepting.
 */
import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CookiePreferences } from '@/components/analytics/CookiePreferences';
import { useConsent } from '@/components/analytics/ConsentProvider';
import { LegalPolicyModal } from '@/components/legal/LegalPolicyModal';
import { cn } from '@/lib/utils';
import type { ConsentCategories } from '@/lib/analytics/consentCookie';
import type { AnalyticsSettings, CookieCategory, LegalContentSettings } from '@/types/organization';

/** Full width and a 44px target on a phone; shared so no action is easier to hit than another. */
const ACTION_CLASS = 'min-h-11 w-full';
export interface CookieConsentGateProps {
  /** Resolved settings for the child organisation being browsed. */
  settings: AnalyticsSettings;
  /** The org's authored legal content, for the cookie policy dialogue. */
  legalContent?: LegalContentSettings;
}
export function CookieConsentGate(t0) {
  const $ = _c(61);
  const {
    settings,
    legalContent
  } = t0;
  const {
    isReady,
    isEnabled,
    isGateOpen,
    isPreferencesOpen,
    categories,
    acceptAll,
    acceptEssentialOnly,
    savePreferences,
    openPreferences,
    closePreferences
  } = useConsent();
  const [draft, setDraft] = useState(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const effective = draft ?? categories;
  const {
    banner
  } = settings;
  const isOpen = isGateOpen || isPreferencesOpen;
  if (!isEnabled || !isReady || !isOpen) {
    return null;
  }
  let t1;
  if ($[0] !== isGateOpen) {
    t1 = event => {
      if (isGateOpen) {
        event.preventDefault();
      }
    };
    $[0] = isGateOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const blockEvent = t1;
  let t2;
  if ($[2] !== closePreferences) {
    t2 = () => {
      setDraft(null);
      closePreferences();
    };
    $[2] = closePreferences;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const dismiss = t2;
  let t3;
  if ($[4] !== effective) {
    t3 = (category, granted) => {
      setDraft({
        ...effective,
        [category]: granted
      });
    };
    $[4] = effective;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleToggle = t3;
  let t4;
  if ($[6] !== dismiss || $[7] !== isGateOpen) {
    t4 = next => {
      if (!next && !isGateOpen) {
        dismiss();
      }
    };
    $[6] = dismiss;
    $[7] = isGateOpen;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = !isGateOpen;
  const t6 = isGateOpen ? "alertdialog" : "dialog";
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("flex max-h-[85vh] flex-col items-stretch gap-4 overflow-y-auto", "top-auto bottom-0 max-w-none translate-y-0 rounded-b-none", "sm:top-0 sm:right-0 sm:bottom-0 sm:left-auto", "sm:h-dvh sm:max-h-none sm:w-[26rem] sm:max-w-[92vw]", "sm:translate-x-0 sm:translate-y-0 sm:rounded-none sm:border-l");
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== banner.heading) {
    t8 = <DialogTitle>{banner.heading}</DialogTitle>;
    $[10] = banner.heading;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== banner.body) {
    t9 = <DialogDescription>{banner.body}</DialogDescription>;
    $[12] = banner.body;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <DialogHeader>{t8}{t9}</DialogHeader>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <button type="button" onClick={() => setPolicyOpen(true)} className="self-start text-sm underline underline-offset-2 hover:no-underline">Read our cookie policy</button>;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== effective || $[19] !== handleToggle || $[20] !== isPreferencesOpen || $[21] !== settings.categories) {
    t12 = isPreferencesOpen ? <CookiePreferences categories={settings.categories} value={effective} onChange={handleToggle} /> : null;
    $[18] = effective;
    $[19] = handleToggle;
    $[20] = isPreferencesOpen;
    $[21] = settings.categories;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== acceptAll) {
    t13 = () => {
      setDraft(null);
      acceptAll();
    };
    $[23] = acceptAll;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== banner.accept_all_label || $[26] !== t13) {
    t14 = <Button className={ACTION_CLASS} onClick={t13}>{banner.accept_all_label}</Button>;
    $[25] = banner.accept_all_label;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== acceptEssentialOnly) {
    t15 = () => {
      setDraft(null);
      acceptEssentialOnly();
    };
    $[28] = acceptEssentialOnly;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== banner.essential_only_label || $[31] !== t15) {
    t16 = <Button className={ACTION_CLASS} onClick={t15}>{banner.essential_only_label}</Button>;
    $[30] = banner.essential_only_label;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== banner.manage_label || $[34] !== banner.save_preferences_label || $[35] !== effective || $[36] !== isPreferencesOpen || $[37] !== openPreferences || $[38] !== savePreferences) {
    t17 = isPreferencesOpen ? <Button className={ACTION_CLASS} variant="outline" onClick={() => {
      savePreferences(effective);
      setDraft(null);
    }}>{banner.save_preferences_label}</Button> : <Button className={ACTION_CLASS} variant="outline" onClick={openPreferences}>{banner.manage_label}</Button>;
    $[33] = banner.manage_label;
    $[34] = banner.save_preferences_label;
    $[35] = effective;
    $[36] = isPreferencesOpen;
    $[37] = openPreferences;
    $[38] = savePreferences;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t14 || $[41] !== t16 || $[42] !== t17) {
    t18 = <div className="flex shrink-0 flex-col gap-2">{t14}{t16}{t17}</div>;
    $[40] = t14;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== blockEvent || $[45] !== t10 || $[46] !== t12 || $[47] !== t18 || $[48] !== t5 || $[49] !== t6) {
    t19 = <DialogContent showCloseButton={t5} role={t6} className={t7} onEscapeKeyDown={blockEvent} onPointerDownOutside={blockEvent} onInteractOutside={blockEvent}>{t10}{t11}{t12}{t18}</DialogContent>;
    $[44] = blockEvent;
    $[45] = t10;
    $[46] = t12;
    $[47] = t18;
    $[48] = t5;
    $[49] = t6;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== t19 || $[52] !== t4) {
    t20 = <Dialog open={true} onOpenChange={t4}>{t19}</Dialog>;
    $[51] = t19;
    $[52] = t4;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  const t21 = policyOpen ? "cookies" : null;
  let t22;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setPolicyOpen(false);
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== legalContent || $[56] !== t21) {
    t23 = <LegalPolicyModal policy={t21} onClose={t22} legalContent={legalContent} zIndexClass="z-[60]" />;
    $[55] = legalContent;
    $[56] = t21;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t20 || $[59] !== t23) {
    t24 = <>{t20}{t23}</>;
    $[58] = t20;
    $[59] = t23;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  return t24;
}